/**
 * The instance id identifies this installation of the app - the browser profile or desktop app it runs in.
 *
 * Per-instance settings (preferred audio devices, window geometry, selected phone) are stored against it on
 * the server, so an id that silently changes takes those settings with it. Local storage is the only owner
 * of the id - read and write it through here so it can never drift from what the rest of the app believes.
 */

/** Local storage key holding the instance id */
const STORAGE_KEY = 'instanceId'

/** Number of characters in a generated instance id */
const ID_LENGTH = 32

/** Characters a generated instance id is built from */
const ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'

/**
 * Generate a random instance id without storing it
 * @returns The generated id
 */
function generate() {
	let length = ID_LENGTH
	let buffer = ''

	while (length) {
		buffer += ID_ALPHABET[Math.floor(Math.random() * ID_ALPHABET.length)]
		length--
	}
	return buffer
}

/**
 * Get the instance id of this installation, generating and storing one on first use
 * @returns The instance id
 */
export function getInstanceId() {
	const id = localStorage.getItem(STORAGE_KEY)

	if (id) { return id }

	return newInstanceId()
}

/**
 * Store an instance id, making it the id this installation identifies itself by
 * - used when returning from an impersonation session, where the id we had before must come back
 * @param id Instance id to store - an empty id is ignored, so the current one is never lost
 */
export function setInstanceId(id: string) {
	if (!id) { return }
	localStorage.setItem(STORAGE_KEY, id)
}

/**
 * Replace the instance id with a freshly generated one
 * - only for becoming a different installation on purpose, as when starting an impersonation session
 * @returns The new instance id
 */
export function newInstanceId() {
	const id = generate()

	localStorage.setItem(STORAGE_KEY, id)
	return id
}
